/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import { action, computed, makeObservable, observable } from 'mobx';
import findLast from 'lodash/findLast';

import type React from 'react';

import { WizardStepState } from '../DialogWizard/entries/wizard-step-state';

import type { WizardStep, OnFinishHandler, WizardProps, WizardStepData } from '../DialogWizard/interfaces';

interface TabConfig {
    title: string;
    disabled: boolean;
    active: boolean;
    onClick: () => void;
}

export class WizardStateStore<T> {
    header: string;
    isEdit: boolean;
    steps: WizardStepState<T>[];
    data: T;
    onFinish: OnFinishHandler<T>;
    wizardProps: WizardProps<T>;

    @observable currentStepId = '';
    protected visitedStepIds = new Set<string>();

    protected stepById: { [key: string]: WizardStepState<T>; };
    @observable protected lastStepWasEnabled = false;
    @observable reloadVar = 0; // mobx force update hack var

    @observable loading = false;
    @observable pending = false;

    isFinishHandling = false;

    constructor(props: WizardProps<T>) {
        this.header = props.title;
        this.isEdit = props.isEdit;
        this.steps = props.steps.map(step => new WizardStepState(step, () => this.rebuildTabConfig()));
        this.data = props.data;
        this.onFinish = props.onFinish;

        this.wizardProps = props;

        this.initSteps();
        makeObservable(this);
    }

    initSteps(): void {
        this.stepById = {};
        this.steps.forEach((step) => {
            this.stepById[step.id] = step;
        });

        this.selectStep(this.getFirstAccessibleStep(), true);
    }

    getWizardStepData(step?: WizardStepState<T>): WizardStepData<T> {
        return {
            isEdit: this.isEdit,
            data: this.data,
            updateTabs: () => this.rebuildTabConfig(),
            onDataChange: newData => this.onDataChange(),
            stepData: step ? step.stepData : this.currentStep.stepData,
        };
    }

    @computed
    get tabsConfig(): TabConfig[] {
        // mobx force update hack
        // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
        const { reloadVar } = this;

        const hasVisitedStepOrFurther = (arr: WizardStepState<T>[], index: number) => {
            for (let i = index; i < arr.length; i++) {
                if (this.visitedStepIds.has(arr[i].id)) {
                    return true;
                }
            }
            return false;
        };

        return this.steps
            .filter(step => step.isHidden === undefined || !step.isHidden(this.getWizardStepData(step)))
            .map((step, i, arr) => ({
                title: step.tabName,
                disabled: !this.isEdit && (!hasVisitedStepOrFurther(arr, i)),
                active: step.id === this.currentStepId,
                onClick: () => {
                    this.selectStepById(step.id);
                },
            }));
    }

    @computed
    get currentStepElement(): React.ReactNode {
        // mobx force update hack
        // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
        const a = this.reloadVar;

        const stepData = this.getWizardStepData();
        stepData.validationState = this.currentStep.validationState;
        return this.currentStep.render(stepData);
    }

    @computed
    get currentStepTitle(): string {
        return this.currentStep.title;
    }

    @computed
    get currentStepDescription(): string {
        return this.currentStep.description || '';
    }

    @computed
    get currentStep(): WizardStepState<T> {
        return this.stepById[this.currentStepId];
    }

    @computed
    get currentPrevActionText(): string {
        return this.stepById[this.currentStepId].prevActionText || this.wizardProps.prevActionText;
    }

    @computed
    get currentNextActionText(): string {
        return this.stepById[this.currentStepId].nextActionText || this.wizardProps.nextActionText;
    }

    @computed
    get currentDoneActionText(): string {
        return this.stepById[this.currentStepId].doneActionText || this.wizardProps.doneActionText;
    }

    @computed
    get currentCancelActionText(): string {
        return this.stepById[this.currentStepId].cancelActionText || this.wizardProps.cancelActionText;
    }

    get cancelActionDisabled(): boolean {
        return this.stepById[this.currentStepId].disableCancelAction;
    }

    @computed
    get isLastStep(): boolean {
        const lastVisibleStep: WizardStepState<T> = findLast(this.steps, (step: WizardStepState<T>) => {
            if (step.isHidden) {
                return !step.isHidden(this.getWizardStepData(step));
            }

            return true;
        });

        return this.currentStepId === lastVisibleStep.id;
    }

    @computed
    get isFirstStep(): boolean {
        const firstVisibleStep = this.steps.find((step) => {
            if (step.isHidden) {
                return !step.isHidden(this.getWizardStepData(step));
            }

            return true;
        });

        return this.currentStepId === firstVisibleStep.id;
    }

    @computed
    get canDone(): boolean {
        return this.isEdit || this.lastStepWasEnabled;
    }

    @action.bound
    setStepId(stepId: string): void {
        this.visitedStepIds.add(stepId);
        this.currentStepId = stepId;
    }

    @action.bound
    goToNextStep(): void {
        const nextStep: WizardStep<T> | null = this.getNextAccessibleStep();

        if (nextStep) {
            this.selectStep(nextStep);
        }
    }

    @action.bound
    goToPrevStep(): void {
        const prevStep: WizardStep<T> | null = this.getPrevAccessibleStep();

        if (prevStep) {
            this.selectStep(prevStep);
        }
    }

    @action.bound
    selectStep(step: WizardStep<T>, skipValidate?: boolean): void {
        if (skipValidate === true) {
            this.handleStepClose();
            this.setStepId(step.id);
            return;
        }

        this.findInvalidStepBefore(step.id)
            .then((invalidStepId) => {
                if (invalidStepId === null) {
                    this.handleStepClose();
                    this.setStepId(step.id);

                    if (this.isLastStep && !this.lastStepWasEnabled) {
                        this.lastStepWasEnabled = true;
                    }

                } else {
                    if (this.currentStepId !== invalidStepId) {
                        this.handleStepClose();
                        this.setStepId(invalidStepId);
                    } else {
                        this.rebuildTabConfig();
                    }
                }
            });
    }

    @action.bound
    selectStepById(stepId: string): void {
        const step = this.steps.find(step => step.id === stepId);
        if (step) {
            this.selectStep(step);
        }
    }

    @action.bound
    rebuildTabConfig(): void {
        this.reloadVar = this.reloadVar + 1;
    }

    @action.bound
    onDataChange(): void {
        this.rebuildTabConfig();
    }

    @action.bound
    setLoading(loading: boolean): void {
        this.loading = loading;
    }

    @action.bound
    setPending(pending: boolean): void {
        this.pending = pending;
    }

    finish(hideAction: () => void): void {
        if (this.isFinishHandling) {
            return;
        }

        this.isFinishHandling = true;

        this.findInvalidStepBefore(null)
            .then((invalidStepId) => {
                if (invalidStepId === null) {
                    if (this.onFinish && !this.loading) {
                        this.setLoading(true);
                        this.onFinish(this.getWizardStepData())
                            .then(() => {
                                this.setLoading(false);
                                hideAction();
                            })
                            .catch(() => {
                                this.setLoading(false);
                                this.isFinishHandling = false;
                            });

                        return;
                    } else {
                        hideAction();
                    }

                } else {
                    if (this.currentStepId !== invalidStepId) {
                        this.handleStepClose();
                        this.setStepId(invalidStepId);
                    } else {
                        this.rebuildTabConfig();
                    }

                    this.isFinishHandling = false;
                }
            });
    }

    protected handleStepClose(): void {
        if (!this.currentStepId) {
            return;
        }

        const oldStep = this.stepById[this.currentStepId];

        if (oldStep.beforeClose) {
            oldStep.beforeClose(this.getWizardStepData());
        }
    }

    protected findInvalidStepBefore(stepId: string): Promise<string|null> {
        this.setLoading(true);
        return (new Promise(async(resolve) => {
            for (let i = 0; i < this.steps.length; i++) {
                const step = this.steps[i];

                if (step.id === stepId) {
                    break;
                }

                const stepData = this.getWizardStepData(step);
                const stepIsHidden = step.isHidden !== undefined && step.isHidden(stepData);
                if (stepIsHidden || step.validate === undefined) {
                    continue;
                }

                const stepValidate = step.validate(stepData);

                if (stepValidate === true) {
                    continue;
                } else if (stepValidate === false) {
                    resolve(step.id);
                }

                const asyncValidateResult = await stepValidate;

                if (asyncValidateResult === true) {
                    continue;
                } else if (asyncValidateResult === false) {
                    resolve(step.id);
                }
            }

            resolve(null);
        }))
            .then((result: string|null) => {
                this.setLoading(false);
                return result;
            });
    }

    protected isStepAfterOther(stepId1: string, stepId2: string): boolean {
        let wasMetStep1 = false;

        if (stepId1 === stepId2) {
            return false;
        }

        this.steps.find((step) => {
            if (step.id === stepId1) {
                wasMetStep1 = true;
            }

            return step.id === stepId2;
        });

        return !wasMetStep1;
    }

    protected getFirstAccessibleStep(): WizardStep<T> {
        const res = this.steps.find(step => step.isHidden === undefined || !step.isHidden(this.getWizardStepData(step)));

        if (res === undefined) {
            throw 'No accessible steps';
        }

        return res;
    }

    protected getNextAccessibleStep(): WizardStep<T> | null {
        let isBeforeCurrentStep = true;
        const res = this.steps.find((step) => {
            const isCurrentStep = step.id === this.currentStepId;
            isBeforeCurrentStep = isBeforeCurrentStep && !isCurrentStep;
            return !isBeforeCurrentStep && !isCurrentStep && (step.isHidden === undefined || !step.isHidden(this.getWizardStepData(step)));
        });

        return res || null;
    }

    protected getPrevAccessibleStep(): WizardStep<T> | null {
        if (this.isFirstStep) {
            return null;
        }

        let prevAccessibleStep: WizardStep<T> = null;
        this.steps.find((step) => {
            const isCurrentStep = step.id === this.currentStepId;
            const isAccessible = step.isHidden === undefined || !step.isHidden(this.getWizardStepData(step));

            if (isCurrentStep) {
                return true;
            }

            if (isAccessible) {
                prevAccessibleStep = step;
            }

            return false;
        });

        return prevAccessibleStep;
    }

}
